import { z } from 'zod/v3';
import {
  transformNull,
  zIsoDate,
  zValidPeriod,
} from '../../../common/utils/schema';
import {
  zDataObjectType,
  zDataObjectGroupType,
} from '../../../dataobject/schema';
import { EntitySchema } from '../../../common/utils/schema/EntitySchema';

export const UserSearchResultSchema = z.object({
  id: z.number(),
  username: z.string(),
});

export const UserRoleFormDataSchema = z.object({
  id: z.number(),
  name: z.string(),
  validPeriod: zValidPeriod(),
  permissions: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
      code: z.string(),
      checkScope: z.boolean().optional(),
    }),
  ),
  accessGroups: z.array(
    z.object({
      id: z.number(),
      name: z.string(),
    }),
  ),
});

const UserViewPermissionFormDataBaseSchema = z.object({
  validPeriod: zValidPeriod(),
});

export const UserViewPermissionFormDataSchema = z.union([
  UserViewPermissionFormDataBaseSchema.extend({
    dataObjectGroupId: z.number(),
    dataObjectDescriptionId: z.null(),
    type: zDataObjectGroupType,
  }).transform(
    ({ dataObjectGroupId, dataObjectDescriptionId, type, ...rest }) => ({
      id: `${type}-${dataObjectGroupId}` as const,
      ...rest,
    }),
  ),
  UserViewPermissionFormDataBaseSchema.extend({
    dataObjectGroupId: z.null(),
    dataObjectDescriptionId: z.number(),
    type: zDataObjectType,
  }).transform(
    ({ dataObjectGroupId, dataObjectDescriptionId, type, ...rest }) => ({
      id: `${type}-${dataObjectDescriptionId}` as const,
      ...rest,
    }),
  ),
]);

export const UserFormDataSchema = EntitySchema.extend({
  id: transformNull(z.number().readonly(), 0),
  firstName: transformNull<string>(z.string(), ''),
  lastName: transformNull<string>(z.string(), ''),
  username: z.string(),
  nationalIdNumber: transformNull<string>(z.string(), ''),
  validFrom: zIsoDate().nullable(),
  validTo: zIsoDate().nullable(),
  viewPermissions: transformNull<
    z.output<typeof UserViewPermissionFormDataSchema>[],
    z.input<typeof UserViewPermissionFormDataSchema>[]
  >(UserViewPermissionFormDataSchema.array(), []),
  roles: UserRoleFormDataSchema.array(),
  isAdmin: z.boolean(),
  isSuperadmin: z.boolean(),
}).transform((data) => {
  const { validFrom, validTo, ...rest } = data;
  return {
    ...rest,
    validPeriod: [validFrom, validTo] as [
      Exclude<typeof validFrom, undefined>,
      Exclude<typeof validTo, undefined>,
    ],
  };
});
